import {useChildShopId, useShopId} from '@aboutyou/config';
import {useLegacyGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {PushNotificationsService_UpdatePushNotificationPreferencesLazy} from '@aboutyou/grpc-clients/grips/protos/push_notifications/v1/push_notifications_service.grpc.lazy';
import {
  CustomerGender,
  Device,
  RequestConfig,
} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
import type {MaybeTokenOrSessionId} from '@aboutyou/user';
import {UserGenders, useOptionalUser} from '@aboutyou/user';

interface UpdatePushNotificationProps {
  device: Device;
  token: string;
  devicePushState: 'granted' | 'denied' | 'notSupported' | 'provisional';
  uuid: string;
  session: MaybeTokenOrSessionId;
}

/*
  Create because its consuming legacy session objects
*/
const getSession = (
  tokenOrSessionId: MaybeTokenOrSessionId,
): RequestConfig['session'] => {
  if (!tokenOrSessionId) {
    return;
  }

  return tokenOrSessionId && 'token' in tokenOrSessionId
    ? {
        $case: 'customerSession' as const,
        customerSession: {authToken: tokenOrSessionId.token},
      }
    : {
        $case: 'guestSession' as const,
        guestSession: {sessionId: (tokenOrSessionId as any)?.sessionId},
      };
};

const getGender = (userGender: UserGenders | undefined) => {
  if (!userGender) {
    return CustomerGender.INVALID;
  }
  switch (userGender) {
    case UserGenders.Female:
      return CustomerGender.FEMALE;
    case UserGenders.Male:
      return CustomerGender.MALE;
    default:
      return CustomerGender.INVALID;
  }
};

export const usePushNotification = () => {
  const pushNotificationService = useLegacyGrpcRequestCallback(
    PushNotificationsService_UpdatePushNotificationPreferencesLazy,
  );
  const childShopId = useChildShopId();
  const shopId = useShopId();
  const user = useOptionalUser();

  const updatePushNotification = ({
    device,
    token,
    devicePushState,
    uuid,
    session,
  }: UpdatePushNotificationProps) => {
    return pushNotificationService.mutateAsync({
      config: {
        childShopId,
        shopId,
        device,
        session: getSession(session),
      },
      FCMRegistrationKey: token!,
      customerGender: getGender(user?.data?.userData?.gender),
      adjustId: '',
      localyticsId: '',
      event: 'open',
      uuid,
      devicePushState,
      PushEnabled: devicePushState === 'granted',
      appVersion: '',
      vendorId: '',
    });
  };

  return {updatePushNotification};
};
